import { apiFetch } from "./apiClient";
import type { MarketDriver, MarketMood, PricingAction } from "./outlookApi";
import type { PmsProviderType } from "./pmsApi";
import type { SyncFrequencyMinutes } from "../types/config";

/**
 * The sidebar's sync indicator, as the server judges it. Mirrors
 * `optivo-service/src/services/pms/syncStatus.ts` — the verdict is weighed
 * there, so the chip and the popover cannot disagree with the server or with
 * each other.
 *
 * These are the verdicts this page was built with, and like the issue kinds
 * below that is not all the server may send: a verdict it gains reaches pages
 * that predate it. `PmsSyncStatus` reads `good` only where the server said
 * `good` and says of a verdict it does not list that it cannot tell
 * (`syncStatus.unknownState`); `PmsSyncStatus.parity.test.ts` holds this list
 * to the server's.
 */
export type SyncVerdict = "good" | "syncing" | "pending" | "warning" | "error";

/**
 * The issue kinds this page was built with. That is not all the server may
 * send: the two deploy separately and a tab stays open for days, so a kind
 * the server gains reaches pages that predate it. `PmsSyncStatus` words such a
 * kind generically, and `PmsSyncStatus.parity.test.ts` holds this list, and
 * the words for it in both languages, to the server's.
 */
export type SyncIssueKind =
    | "lastRunFailed"
    | "lastRunInterrupted"
    | "overdue"
    | "pmsUnreachable"
    | "lastPushFailed"
    /**
     * The last price update landed, short only of what Optivo itself held
     * back: nothing in it failed. One kind whether the update was a send or a
     * restore; `push.lastRestoresPriceList` says which.
     */
    | "lastPushHeldBack"
    | "pollerPaused";

export interface SyncIssue {
    kind: SyncIssueKind;
    /** The recorded fault, where one was recorded. */
    message?: string;
}

/**
 * Tonight, as the rail states it: what Optivo did with tonight's prices and
 * how tonight's market is. Mirrors
 * `optivo-service/src/services/pms/syncStatusTonight.ts`, and
 * `PmsSyncStatus.parity.test.ts` holds the two field for field.
 *
 * The words are the dashboard outlook's own (`outlookApi.ts`), so the rail and
 * the dashboard cannot call one night two things. Like every list the server
 * answers from, they are the values this page was built with and not all it
 * may be sent: `components/pms/tonightReading.ts` reads each as a word it has
 * or as nothing, and says less of a value it does not list.
 */
export interface SyncStatusTonight {
    /** The property's own date this reading is for — tonight, `YYYY-MM-DD`. */
    date: string;
    /** What Optivo did with tonight's prices over the last day; null where nothing can be claimed. */
    pricing: PricingAction | null;
    /** Tonight's local market; null where no market can be read. */
    market: MarketMood | null;
    /** What is behind a market that is not calm, strongest first — at most two. */
    marketDrivers: MarketDriver[];
    /** When the engine last calculated tonight's prices; null where it never has. */
    calculatedAt: string | null;
    /**
     * Whether calculated prices leave for the PMS on their own: the schedule
     * is enabled and sends prices. Not `push.autoSyncEnabled`, which is also
     * true of a schedule that sends minimum stays alone, where the prices
     * described here reach no guest until somebody sends them.
     */
    autoSendsPrices: boolean;
}

export interface PmsSyncStatusOn {
    mode: "on";
    /**
     * The connection this status describes — what the popover's auto-sync
     * dialog configures, so it can never be a different property from the one
     * the status is about.
     */
    connectionId: number;
    providerType: PmsProviderType;
    syncFrequencyMinutes: SyncFrequencyMinutes;
    verdict: SyncVerdict;
    /** Everything wrong, most severe first — empty when the verdict is good. */
    issues: SyncIssue[];
    isSyncing: boolean;
    lastRunStartedAt: string | null;
    lastRunFinishedAt: string | null;
    nextRunAt: string | null;
    lastChangedDateCount: number;
    lastRecalcCount: number;
    health: { status: string; checkedAt: string | null };
    push: {
        autoSyncEnabled: boolean;
        /**
         * Whether calculated prices leave for the PMS on their own: the
         * schedule is enabled and sends prices, as it stands when the status
         * answers. The server states the same on `tonight.autoSendsPrices`;
         * this one is read where a reading is carried over from an earlier
         * status (`useSyncStatus`). Absent from a server that predates it.
         */
        autoSendsPrices?: boolean;
        lastAt: string | null;
        /**
         * The last push's status as its log row stores it. `failed` is every
         * push some cell of which did not land, so it is read with
         * `lastHeldBack` beside it.
         */
        lastStatus: "completed" | "failed" | null;
        lastEntries: number;
        lastError: string | null;
        /**
         * How many cells of the last push Optivo itself held back, where that
         * is all the push was short of: a count above zero is a push that is
         * logged as failed and is no failure. Absent from a server that
         * predates it, where a failed push is read as one.
         */
        lastHeldBack?: number;
        /**
         * Whether the last push put a price list back: a restore, or a retry
         * of one. It words what becomes of a night the push held back. A
         * later send tries a send's again, and nothing tries a restore's,
         * since a restore commits no drafts.
         *
         * Only `true` is a restore. Absent from a server that predates it and
         * null where the row does not say, and both are read as a send's, as
         * every status was read.
         */
        lastRestoresPriceList?: boolean | null;
    };
    /**
     * Tonight's pricing and market, for the rail's third line, the folded
     * rail's tooltip and the popover's "Today" section.
     *
     * Only an object is a reading. Absent from a server that predates it, and
     * from one that has the reading switched off, could not compose it in
     * time or failed to; null is tolerated the same way, though the server
     * leaves the field out and never sends one. The page then says nothing
     * about tonight, and the indicator reads as it did before the field.
     */
    tonight?: SyncStatusTonight | null;
}

/** `off` is a property with no active pacing strategy: nothing to say. */
export type PmsSyncStatus = { mode: "off" } | PmsSyncStatusOn;

export async function fetchSyncStatus(): Promise<PmsSyncStatus> {
    const res = await apiFetch("/api/sync-status");
    if (!res.ok) throw new Error(`fetchSyncStatus failed: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to load sync status");
    return data.status;
}
